import type { SyncStatus } from '@cadencia/contracts';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from '../auth/AuthProvider.js';
import { getSupabase } from '../auth/supabaseClient.js';
import { logger } from '../lib/logger.js';
import { isOnline } from '../platform/capabilities.js';
import { getDeviceId } from '../platform/device.js';
import { db, destroyLocalDatabase, initSyncState } from './db.js';
import { revisarAvisos } from './services/notificationService.js';
import { createSupabaseTransport } from './sync/supabaseTransport.js';
import {
  pendingOpsCount,
  reclasificarRechazosPermanentes,
  runSyncOnce,
} from './sync/syncEngine.js';
import type { WriteContext } from './writes.js';

const SYNC_INTERVAL_MS = 60_000;
const OWNER_KEY = 'ownerUserId';

/**
 * Un conflicto que la ultima sincronizacion dejo sin resolver.
 *
 * Lo minimo para poder enseñarlo: que entidad, que fila y por que lo dijo el
 * motor. No se inventa nada que `onConflict` no entregue ya.
 */
export interface ConflictoVisible {
  entity: string;
  id: string;
  reason: string;
}

export interface AppDataState {
  ready: boolean;
  ctx: WriteContext | null;
  status: SyncStatus;
  pending: number;
  lastError: string | null;
  /** Conflictos de la ultima sincronizacion. Vacio si no hubo. */
  conflicts: ConflictoVisible[];
  syncNow: () => Promise<void>;
  refreshPending: () => Promise<void>;
}

const DataContext = createContext<AppDataState | null>(null);

export function DataProvider({ children }: { children: ReactNode }): ReactNode {
  const { userId, status: authStatus } = useAuth();
  const [ctx, setCtx] = useState<WriteContext | null>(null);
  const [ready, setReady] = useState(false);
  const [status, setStatus] = useState<SyncStatus>('offline');
  const [pending, setPending] = useState(0);
  const [lastError, setLastError] = useState<string | null>(null);
  const [conflicts, setConflicts] = useState<ConflictoVisible[]>([]);
  const running = useRef(false);

  /*
   * ¿Sigue montado esto?
   *
   * `refreshPending` se llama al final de operaciones que escriben —borrar un
   * equipo, aplicar una propuesta— y esas operaciones sobreviven a la pantalla
   * que las lanzó: basta con salir de Gimnasio justo después de pulsar. El
   * `setPending` llegaba entonces a un árbol desmontado, y React lo convierte
   * en una promesa rechazada sin dueño («window is not defined» en los tests,
   * silencio en el navegador). Lo cazó CI el 22/09/2026, no el desarrollo:
   * depende de cuánto tarde Dexie, así que en una máquina rápida no se ve.
   */
  const montado = useRef(true);
  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  useEffect(() => {
    if (!userId) {
      setCtx(null);
      setReady(authStatus === 'signed_out' || authStatus === 'unconfigured');
      return;
    }

    let cancelled = false;
    void (async () => {
      // Si la base local pertenece a otro usuario, se destruye antes de nada
      // (docs/SYNC_ENGINE.md §8).
      const owner = await db.meta.get(OWNER_KEY);
      if (owner && owner.value !== userId) {
        logger.warn('db', 'DB_OWNER_MISMATCH', 'Base local de otro usuario: se destruye');
        await destroyLocalDatabase();
        globalThis.location.reload();
        return;
      }
      await db.meta.put({ key: OWNER_KEY, value: userId });

      const deviceId = await getDeviceId();
      await initSyncState(deviceId);
      if (cancelled) return;

      setCtx({ db, userId, deviceId });

      // Antes de contar lo pendiente: una operacion que el servidor ya rechazo
      // con un codigo definitivo no es un cambio pendiente, es un cambio
      // imposible, y contarla infla un numero que no baja nunca. Va aqui y no
      // en la sincronizacion porque el dispositivo puede llevar semanas sin
      // conseguir subir nada, que es justo el caso que trajo esto.
      await reclasificarRechazosPermanentes(db);
      setPending(await pendingOpsCount(db));
      setReady(true);

      // Los avisos del §8. Se revisan al abrir, que en Windows es el UNICO
      // momento posible —una PWA no puede programar nada sin push— y en Android
      // es ademas cuando se refresca lo ya programado.
      //
      // Con `catch` propio y despues de `setReady`: un aviso que no sale es una
      // molestia; una pantalla que no arranca por no poder avisar es un fallo.
      try {
        await revisarAvisos(userId);
      } catch (caught) {
        logger.warn('ui', 'NOTIF_REVIEW_FAILED', 'No se pudieron revisar los avisos', {
          message: caught instanceof Error ? caught.message : String(caught),
        });
      }
    })().catch((caught: unknown) => {
      // Un fallo al abrir la base no puede dejar la pantalla cargando en
      // silencio: se dice y se deja pasar a la aplicación.
      if (cancelled) return;
      setLastError(caught instanceof Error ? caught.message : 'No se pudieron abrir tus datos.');
      setStatus('error');
      setReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, [userId, authStatus]);

  const refreshPending = useCallback(async () => {
    const cuenta = await pendingOpsCount(db);
    if (montado.current) setPending(cuenta);
  }, []);

  const syncNow = useCallback(async () => {
    if (running.current) return;
    const supabase = getSupabase();
    if (!supabase || !userId) {
      setStatus(isOnline() ? 'pending' : 'offline');
      return;
    }
    if (!isOnline()) {
      setStatus('offline');
      return;
    }

    running.current = true;
    setStatus('syncing');
    try {
      // El callback RECOGE; no decide el estado.
      //
      // Antes ponia `setStatus('conflict')` aqui mismo y tres lineas mas abajo
      // se ejecutaba `setStatus(error ? 'error' : 'synced')` sin condicion, asi
      // que `conflict` era inalcanzable: el motor detectaba el choque, lo
      // registraba, y la pantalla decia «sincronizado». `outcome.conflicts` se
      // contaba y no lo miraba nadie.
      //
      // Ahora el estado se decide UNA sola vez, al final, a partir del
      // resultado completo.
      const detectados: ConflictoVisible[] = [];
      const outcome = await runSyncOnce({
        db,
        transport: createSupabaseTransport(supabase),
        onConflict: (entity, id, reason) => {
          detectados.push({ entity, id, reason });
          logger.warn('sync', 'SYNC_CONFLICT_VISIBLE', 'Conflicto pendiente de revision', {
            entity,
            id,
            reason,
          });
        },
      });
      const remaining = await pendingOpsCount(db);
      setPending(remaining);
      setLastError(outcome.error);
      setConflicts(detectados);

      // Precedencia explicita: error > conflicto > pendiente > sincronizado.
      // Un conflicto pesa mas que quedar pendiente porque pendiente se arregla
      // solo con la siguiente vuelta y un conflicto no.
      setStatus(
        outcome.error !== null
          ? 'error'
          : outcome.conflicts > 0 || detectados.length > 0
            ? 'conflict'
            : remaining > 0
              ? 'pending'
              : 'synced',
      );
    } catch (caught) {
      if (montado.current) {
        setLastError(
          caught instanceof Error
            ? caught.message
            : 'No se pudo sincronizar. Tus cambios siguen en este dispositivo.',
        );
        setStatus('error');
      }
    } finally {
      running.current = false;
    }
  }, [userId]);

  // Disparadores: arranque, vuelta de conexion, foco y cada 60 s.
  useEffect(() => {
    if (!ready || !userId) return;
    void syncNow();

    const interval = globalThis.setInterval(() => void syncNow(), SYNC_INTERVAL_MS);
    const onOnline = (): void => void syncNow();
    const onOffline = (): void => setStatus('offline');
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') void syncNow();
    };

    globalThis.addEventListener('online', onOnline);
    globalThis.addEventListener('offline', onOffline);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      globalThis.clearInterval(interval);
      globalThis.removeEventListener('online', onOnline);
      globalThis.removeEventListener('offline', onOffline);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [ready, userId, syncNow]);

  const value = useMemo<AppDataState>(
    () => ({ ready, ctx, status, pending, lastError, conflicts, syncNow, refreshPending }),
    [ready, ctx, status, pending, lastError, conflicts, syncNow, refreshPending],
  );

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export function useAppData(): AppDataState {
  const context = useContext(DataContext);
  if (!context) throw new Error('useAppData debe usarse dentro de DataProvider');
  return context;
}
